import { useEffect, useMemo, useRef, useState } from "react";

const FRAME_SIZE = 144;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getCoverSize = (frameSize, naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) {
    return { width: frameSize, height: frameSize };
  }

  const scale = Math.max(frameSize / naturalWidth, frameSize / naturalHeight);
  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
  };
};

const clampOffset = (offset, scaledSize, frameSize) => {
  const maxOffsetX = Math.max(0, (scaledSize.width - frameSize) / 2);
  const maxOffsetY = Math.max(0, (scaledSize.height - frameSize) / 2);

  return {
    x: clamp(offset.x, -maxOffsetX, maxOffsetX),
    y: clamp(offset.y, -maxOffsetY, maxOffsetY),
  };
};

const StoreSquarePreview = ({
  imageSrc,
  label = "Store Square Preview",
  helperText = "Drag to reposition. Use zoom to simulate the square crop.",
}) => {
  const dragStateRef = useRef(null);
  const [imageMeta, setImageMeta] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!imageSrc) {
      setImageMeta({ width: 0, height: 0 });
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      return;
    }

    const image = new Image();
    image.onload = () => {
      setImageMeta({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
    };
    image.src = imageSrc;
  }, [imageSrc]);

  const baseSize = useMemo(
    () => getCoverSize(FRAME_SIZE, imageMeta.width, imageMeta.height),
    [imageMeta.height, imageMeta.width],
  );

  const scaledSize = useMemo(
    () => ({
      width: baseSize.width * zoom,
      height: baseSize.height * zoom,
    }),
    [baseSize.height, baseSize.width, zoom],
  );

  useEffect(() => {
    setOffset((currentOffset) => clampOffset(currentOffset, scaledSize, FRAME_SIZE));
  }, [scaledSize]);

  const handleZoomChange = (event) => {
    const nextZoom = Number(event.target.value);
    setZoom(nextZoom);
  };

  const handlePointerDown = (event) => {
    if (!imageSrc) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: offset,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const nextOffset = clampOffset(
      {
        x: dragState.startOffset.x + (event.clientX - dragState.startX),
        y: dragState.startOffset.y + (event.clientY - dragState.startY),
      },
      scaledSize,
      FRAME_SIZE,
    );

    setOffset(nextOffset);
  };

  const handlePointerUp = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  if (!imageSrc) {
    return null;
  }

  const zoomPercent = Math.round(((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100);

  return (
    <div style={{ width: `${FRAME_SIZE}px` }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span
          style={{
            display: "inline-flex",
            padding: "2px 9px",
            borderRadius: "999px",
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            backgroundColor: "#e0e7ff",
            color: "#4338ca",
          }}
        >
          1:1
        </span>
        <span className="fw-semibold" style={{ color: "#334155", fontSize: "0.9rem", lineHeight: 1.2 }}>
          {label}
        </span>
      </div>
      <div
        role="presentation"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          width: `${FRAME_SIZE}px`,
          height: `${FRAME_SIZE}px`,
          position: "relative",
          overflow: "hidden",
          borderRadius: "14px",
          border: "1px solid rgba(15, 23, 42, 0.12)",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.08)",
          background:
            "linear-gradient(135deg, rgba(241, 245, 249, 1), rgba(226, 232, 240, 1))",
          cursor: "grab",
          touchAction: "none",
        }}
      >
        <img
          src={imageSrc}
          alt={label}
          draggable={false}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: `${baseSize.width}px`,
            height: `${baseSize.height}px`,
            maxWidth: "none",
            userSelect: "none",
            transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            objectFit: "cover",
          }}
        />
      </div>

      <div className="mt-3" style={{ width: `${FRAME_SIZE}px` }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="text-muted fw-semibold d-flex align-items-center gap-1">
            <i className="bi bi-zoom-in" style={{ fontSize: "0.8rem" }} />
            Zoom
            <span className="text-muted" style={{ fontWeight: 400 }}>
              {zoomPercent}%
            </span>
          </small>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-nowrap"
            onClick={() => {
              setZoom(MIN_ZOOM);
              setOffset({ x: 0, y: 0 });
            }}
          >
            Reset
          </button>
        </div>
        <input
          type="range"
          className="form-range mb-1"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step="0.01"
          value={zoom}
          onChange={handleZoomChange}
        />
        <small className="text-muted d-block" style={{ lineHeight: 1.35 }}>
          {helperText}
        </small>
      </div>
    </div>
  );
};

export default StoreSquarePreview;
